import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import DashboardMock from '../components/landing/DashboardMock';
import {
  ChevronDown,
  ChevronLeft,
  CreditCard,
  Layout,
  Users,
  Mail,
  BarChart3,
  Sparkles,
  ArrowLeft,
  CheckCircle,
  Phone,
  MessageCircle,
  Globe,
  Shield,
  Headphones,
  Zap,
  Clock,
  Heart,
} from 'lucide-react';

/* ──────────────────────────────────────────────
   Intersection Observer hook for scroll animations
   ────────────────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      // Extend the root far above the viewport so sections the user has
      // already jumped past (anchor links, fast scroll) still count as
      // visible instead of staying stuck at opacity-0.
      { threshold, rootMargin: '9999px 0px 0px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

/* ──────────────────────────────────────────────
   Animated counter hook
   ────────────────────────────────────────────── */
function useAnimatedCounter(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

/* ──────────────────────────────────────────────
   Data
   ────────────────────────────────────────────── */
const FEATURES = [
  {
    icon: CreditCard,
    title: 'ניהול תרומות וסליקה',
    description:
      'קבלו תרומות בקלות עם אינטגרציית טרנזילה מלאה. סליקת אשראי, הוראות קבע, קבלות אוטומטיות ומעקב בזמן אמת.',
    color: 'blue',
  },
  {
    icon: Layout,
    title: 'דפי נחיתה וקמפיינים',
    description:
      'בנו דפי גיוס מרהיבים בגרירה ושחרור. עיצוב מותאם אישית, טפסים חכמים ומעקב המרות מובנה.',
    color: 'purple',
  },
  {
    icon: Users,
    title: 'ניהול אנשי קשר ומתנדבים',
    description:
      'CRM מלא לעמותות — פרופילים מפורטים, סגמנטציה חכמה, היסטוריית תרומות וניהול מתנדבים במקום אחד.',
    color: 'green',
  },
  {
    icon: Mail,
    title: 'דיוור, SMS ו-WhatsApp',
    description:
      'שלחו דיוורים מעוצבים, הודעות SMS ו-WhatsApp לתורמים ומתנדבים. תזמון אוטומטי ומעקב פתיחות.',
    color: 'pink',
  },
  {
    icon: BarChart3,
    title: 'דוחות ופיננסים',
    description:
      'דשבורד אנליטי מקיף — דוחות תרומות, תחזיות הכנסות, ניתוח קמפיינים וייצוא לאקסל בלחיצה.',
    color: 'amber',
  },
  {
    icon: Sparkles,
    title: 'AI חכם',
    description:
      'עוזר בינה מלאכותית מובנה שכותב לכם תוכן, מנתח נתונים, מציע אסטרטגיות גיוס ועונה על שאלות.',
    color: 'indigo',
  },
];

const FEATURE_COLORS: Record<string, { bg: string; icon: string; border: string }> = {
  blue: { bg: 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25', icon: 'text-white', border: 'hover:border-blue-300' },
  purple: { bg: 'bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg shadow-purple-500/25', icon: 'text-white', border: 'hover:border-purple-300' },
  green: { bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/25', icon: 'text-white', border: 'hover:border-emerald-300' },
  pink: { bg: 'bg-gradient-to-br from-pink-500 to-rose-500 shadow-lg shadow-pink-500/25', icon: 'text-white', border: 'hover:border-pink-300' },
  amber: { bg: 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/25', icon: 'text-white', border: 'hover:border-amber-300' },
  indigo: { bg: 'bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/25', icon: 'text-white', border: 'hover:border-indigo-300' },
};

const STEPS = [
  {
    num: '01',
    title: 'פתחו חשבון ניסיון חינמי',
    description: 'הירשמו תוך דקה — ללא כרטיס אשראי. מקבלים 7 ימים מלאים לחקור את כל הפיצ׳רים ללא הגבלה.',
    icon: Heart,
    badge: '7 ימים חינם',
  },
  {
    num: '02',
    title: 'הגדרת המערכת',
    description: 'נעזור לכם להתחבר, לייבא נתונים ולהגדיר את הכל בדיוק כמו שצריך.',
    icon: Zap,
    badge: '',
  },
  {
    num: '03',
    title: 'התחילו לגייס',
    description: 'הפעילו קמפיינים, קבלו תרומות וצמחו — אנחנו כאן לאורך כל הדרך.',
    icon: Heart,
    badge: '',
  },
];

interface PricingTier {
  name: string;
  price: number;
  yearlyPrice: number;
  description: string;
  features: string[];
  popular?: boolean;
}

const PRICING: PricingTier[] = [
  {
    name: 'בייסיק',
    price: 199,
    yearlyPrice: 169,
    description: 'לעמותות בתחילת הדרך.',
    features: [
      'עד 100 קבלות תרומה/תשלום',
      '2 משתמשים עם מידור הרשאות',
      '500 SMS חד פעמי',
      '2,000 מיילים חד פעמי',
      'עד 1,000 אנשי קשר במאגר',
      'ניהול הכנסות והוצאות',
      'בניית קמפיינים מבוססי AI ללא הגבלה',
      'דפי נחיתה ותרומה',
      'דוחות בסיסיים',
      'התממשקות ל-WhatsApp',
      'בניית תקציב',
      'ניהול יומן משותף לעמותה',
      'ניהול מתנדבים',
    ],
  },
  {
    name: 'פרו',
    price: 399,
    yearlyPrice: 339,
    description: 'עמותות שכבר באמצע הדרך..',
    features: [
      'עד 500 קבלות תרומה/תשלום',
      '4 משתמשים עם מידור הרשאות',
      '1,000 SMS חודשי',
      '4,000 מיילים חודשי',
      'עד 5,000 אנשי קשר במאגר',
      'ניהול הכנסות והוצאות',
      'בניית קמפיינים מבוססי AI ללא הגבלה',
      'דפי נחיתה ותרומה מבוססי AI',
      'דוחות מפורטים',
      'התממשקות ל-WhatsApp',
      'בניית תקציב כולל דוחות פיננסים',
      'ניהול יומן משותף לעמותה',
      'ניהול מתנדבים',
    ],
    popular: true,
  },
  {
    name: 'ארגוני',
    price: 899,
    yearlyPrice: 764,
    description: 'לעמותות גדולות ומבוססות',
    features: [
      'עד 2,200 קבלות תרומה/תשלום',
      '10 משתמשים עם מידור הרשאות',
      '2,500 SMS חודשי',
      '10,000 מיילים חודשי',
      'אנשי קשר וקבוצות ללא הגבלה',
      'ניהול הכנסות והוצאות',
      'בניית קמפיינים מבוססי AI ללא הגבלה',
      'דפי נחיתה ותרומה מבוססי AI',
      'דוחות מפורטים',
      'התממשקות ל-WhatsApp',
      'בניית תקציב כולל דוחות פיננסים',
      'ניהול יומן משותף לעמותה',
      'מרכז ידע חי ומתעדכן',
      'ניהול מתנדבים',
    ],
  },
];

interface FaqItem {
  q: string;
  a: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'כמה עולה עמוטק?',
    a: 'יש לנו שלוש תוכניות החל מ-₪199 לחודש. לכל תוכנית יש תקופת ניסיון חינמית. צרו איתנו קשר ונמצא את התוכנית המתאימה לכם.',
  },
  {
    q: 'האם יש תקופת ניסיון חינמית?',
    a: 'בהחלט! אנחנו מציעים 7 ימי ניסיון חינמיים בכל התוכניות, ללא צורך בכרטיס אשראי. תוכלו לבדוק את כל הפיצ\'רים ולהחליט.',
  },
  {
    q: 'איך מחברים סליקה?',
    a: 'אנחנו עובדים עם טרנזילה — חיבור הסליקה לוקח פחות מ-5 דקות. פשוט מזינים את פרטי החשבון וזה עובד. אנחנו גם תומכים בהוראות קבע.',
  },
  {
    q: 'האם אפשר לייבא נתונים ממערכת אחרת?',
    a: 'כן! אנחנו תומכים בייבוא מאקסל, CSV ומערכות נפוצות אחרות. צוות ההטמעה שלנו יעזור לכם בתהליך ויוודא שהכל עובר בצורה חלקה.',
  },
  {
    q: 'מה קורה עם האבטחה והפרטיות?',
    a: 'האבטחה בראש סדר העדיפויות שלנו. אנחנו משתמשים בהצפנת SSL, שומרים נתונים בשרתים מאובטחים בישראל ועומדים בתקני אבטחה מחמירים.',
  },
  {
    q: 'האם יש תמיכה בעברית?',
    a: 'המערכת כולה בעברית מלאה, כולל תמיכת RTL מושלמת. גם צוות התמיכה שלנו דובר עברית ואנגלית וזמין בימים א\'-ה\' בין 9:00-18:00.',
  },
];

const FOOTER_LINKS = {
  product: {
    title: 'מוצר',
    links: [
      { label: 'תכונות', href: '#features' },
      { label: 'תמחור', href: '#pricing' },
      { label: 'אינטגרציות', href: '#features' },
      { label: 'מה חדש', href: '#' },
    ],
  },
  company: {
    title: 'חברה',
    links: [
      { label: 'אודות', href: '#about' },
      { label: 'צור קשר', href: '/contact' },
      { label: 'קריירה', href: '#' },
      { label: 'בלוג', href: '#' },
    ],
  },
  legal: {
    title: 'משפטי',
    links: [
      { label: 'תנאי שימוש', href: '/terms' },
      { label: 'מדיניות פרטיות', href: '/privacy' },
      { label: 'נגישות', href: '/accessibility' },
      { label: 'אבטחה', href: '#' },
    ],
  },
  community: {
    title: 'קהילה',
    links: [
      { label: 'מרכז ידע', href: '/knowledge' },
      { label: 'מדריכים', href: '#' },
      { label: 'סרטוני הדרכה', href: '#' },
      { label: 'פורום', href: '#' },
    ],
  },
};

/* ──────────────────────────────────────────────
   FAQ Accordion Item
   ────────────────────────────────────────────── */
const FaqAccordionItem: React.FC<{ item: FaqItem; isOpen: boolean; onToggle: () => void }> = ({
  item,
  isOpen,
  onToggle,
}) => (
  <div className="border-b border-gray-200 last:border-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 px-6 text-right hover:bg-gray-50 transition-colors duration-200"
      aria-expanded={isOpen}
    >
      <span className="text-lg font-semibold text-gray-900">{item.q}</span>
      <ChevronDown
        size={20}
        className={`text-gray-500 transition-transform duration-300 flex-shrink-0 mr-4 ${
          isOpen ? 'rotate-180' : ''
        }`}
      />
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ${
        isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <p className="px-6 pb-5 text-gray-600 leading-relaxed">{item.a}</p>
    </div>
  </div>
);

/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */
const LandingPageV2: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Section refs for scroll animation
  const heroStats = useInView(0.2);
  const logosSection = useInView(0.3);
  const featuresHeader = useInView(0.2);
  const stepsSection = useInView(0.15);
  const pricingSection = useInView(0.1);
  const faqSection = useInView(0.15);
  const ctaSection = useInView(0.2);
  const shotsSection = useInView(0.1);

  // Feature cards — individual refs
  const feat0 = useInView(0.15);
  const feat1 = useInView(0.15);
  const feat2 = useInView(0.15);
  const feat3 = useInView(0.15);
  const feat4 = useInView(0.15);
  const feat5 = useInView(0.15);
  const featureRefs = [feat0, feat1, feat2, feat3, feat4, feat5];

  // Animated counters
  const orgs = useAnimatedCounter(500, 2000, heroStats.isVisible);
  const donors = useAnimatedCounter(50000, 2500, heroStats.isVisible);
  const raised = useAnimatedCounter(10, 2000, heroStats.isVisible);

  const toggleFaq = useCallback(
    (i: number) => setOpenFaq((prev) => (prev === i ? null : i)),
    []
  );

  return (
    <div dir="rtl" className="min-h-screen bg-white text-gray-900 font-heebo">
      {/* ── SEO Meta ── */}
      {/* Using document.title since react-helmet is not installed */}
      <DocumentHead />

      {/* ── Header ── */}
      <PublicHeader />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 1 — HERO
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative pt-20 pb-0 px-6 overflow-hidden bg-[#0A1130]">
        {/* Aurora glows */}
        <div className="absolute -top-48 right-[-15%] w-[720px] h-[720px] bg-blue-600/35 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-32 left-[-12%] w-[560px] h-[560px] bg-violet-600/25 rounded-full blur-[130px] pointer-events-none" />
        <div className="absolute -bottom-24 right-1/3 w-[420px] h-[420px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 80%)',
          }}
        />

        <div className="relative max-w-6xl mx-auto">
          {/* ── Split layout: text RIGHT + mockup LEFT ── */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">

            {/* ── RIGHT: Text + CTA ── */}
            <div className="flex-1 text-right order-1 lg:order-2 pt-0 lg:pt-0">

              {/* Eyebrow badge */}
              <div className="animate-fade-in-up inline-flex items-center gap-2 px-4 py-1.5 mb-6 bg-white/[0.07] border border-white/15 rounded-full backdrop-blur-sm">
                <Sparkles size={14} className="text-amber-400" />
                <span className="text-blue-100 text-sm font-semibold">הפלטפורמה החכמה לעמותות בישראל</span>
              </div>

              {/* Headline */}
              <h1 className="animate-fade-in-up text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.15] tracking-tight mb-5">
                ניהול עמותות חכם
                <br />
                <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                  הכל במקום אחד
                </span>
              </h1>

              {/* Subtitle */}
              <p className="animate-fade-in-up animation-delay-200 text-lg text-slate-300 leading-relaxed mb-8 max-w-lg">
                תרומות, קמפיינים, CRM, דיוור, דוחות ובינה מלאכותית — מערכת אחת שנבנתה במיוחד לעמותות ישראליות.
              </p>

              {/* CTA Buttons */}
              <div className="animate-fade-in-up animation-delay-200 flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-gradient-to-l from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-gray-900 text-base font-bold rounded-2xl shadow-[0_8px_30px_rgba(245,158,11,0.35)] hover:shadow-[0_12px_40px_rgba(245,158,11,0.45)] transition-all duration-300 hover:scale-[1.03]"
                >
                  פתח חשבון בחינם
                  <ArrowLeft size={18} />
                </Link>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/[0.06] backdrop-blur-sm border border-white/20 text-white text-base font-semibold rounded-2xl hover:bg-white/[0.12] hover:border-white/30 transition-all duration-300"
                >
                  למד עוד
                  <ChevronDown size={18} className="animate-bounce" />
                </a>
              </div>

              {/* Trust badges */}
              <div className="animate-fade-in-up animation-delay-400 flex flex-wrap gap-3 mb-10">
                {[
                  { icon: '🇮🇱', text: 'מיוצר בישראל' },
                  { icon: '✅', text: 'ביטול בכל עת' },
                  { icon: '🔒', text: 'סליקה מאובטחת PCI' },
                ].map(b => (
                  <span key={b.text} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.07] border border-white/15 rounded-full text-white/80 text-xs font-medium">
                    <span>{b.icon}</span>{b.text}
                  </span>
                ))}
              </div>

            </div>

            {/* ── LEFT: Dashboard mockup (browser window frame) ── */}
            <div className="flex-1 order-2 lg:order-1 w-full lg:w-auto animate-fade-in-up animation-delay-400">
              <div className="relative">
                {/* Floating notification card */}
                <div
                  className="absolute -top-5 -left-2 sm:-left-6 z-20 flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-white/95 backdrop-blur shadow-xl border border-white/60"
                  style={{ animation: 'floatY 4s ease-in-out infinite' }}
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-base">💚</div>
                  <div dir="rtl">
                    <p className="text-[11px] font-bold text-gray-900 leading-tight">תרומה חדשה התקבלה</p>
                    <p className="text-[11px] text-emerald-600 font-black leading-tight">₪850 — קמפיין סוף שנה</p>
                  </div>
                </div>

                {/* Browser window frame */}
                <div className="rounded-2xl overflow-hidden border border-white/15 bg-[#0f172a] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]">
                  {/* Chrome bar */}
                  <div dir="ltr" className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] border-b border-white/10">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                      <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                      <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                    </div>
                    <div className="flex-1 flex justify-center">
                      <div className="w-full max-w-[220px] rounded-md bg-white/10 text-white/50 text-[11px] text-center py-1 font-medium">app.amutech.co.il</div>
                    </div>
                    <div className="w-10" />
                  </div>
                  {/* Screen */}
                  <div style={{ position: 'relative', aspectRatio: '16 / 10', overflow: 'hidden', background: '#f1f5f9' }}>
                  <div style={{ width: '118%', height: '118%', transform: 'scale(0.85)', transformOrigin: 'top right' }}>
                  <DashboardMock />
                  </div>
                  </div>{/* /screen */}
                </div>{/* /browser window */}
              </div>{/* /relative */}
            </div>{/* /column */}

          </div>

          <div className="py-8" />

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 1.5 — APP PREVIEW MOCKUP
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-10px); }
        }
        @keyframes appScroll {
          0%   { transform: translateY(0); }
          45%  { transform: translateY(-48%); }
          50%  { transform: translateY(-48%); }
          95%  { transform: translateY(0); }
          100% { transform: translateY(0); }
        }
        @keyframes phoneScroll {
          0%   { transform: translateY(0); }
          40%  { transform: translateY(-44%); }
          50%  { transform: translateY(-44%); }
          90%  { transform: translateY(0); }
          100% { transform: translateY(0); }
        }
        .app-scroll  { animation: appScroll  10s ease-in-out infinite; }
        .phone-scroll{ animation: phoneScroll 8s  ease-in-out infinite; }
      `}</style>
      <section className="relative bg-gradient-to-b from-[#0A1130] via-[#101a45] to-gray-50 pb-16 px-6 overflow-hidden">
        {/* Section heading */}
        <div className="text-center mb-10 pt-4">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
            תכנן. עקוב. הצלח.
          </h2>
          <p className="text-blue-100 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            הגדר את תכנית העבודה והתקציב של העמותה — המערכת תעקוב בזמן אמת ותתריע כשצריך.
          </p>
        </div>
        <div className="relative flex items-end justify-center" style={{ gap: 0, maxWidth: 900, margin: '0 auto' }}>

          {/* ── iMac mockup with finance content ── */}
          <div className="relative flex-shrink-0" style={{ width: '80%' }}>
            <img src="/imac-mockup-empty.png" alt="Amutech" style={{ width: '100%', height: 'auto', filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.4))' }} />
            {/* Finance content inside the screen */}
            <div style={{ position: 'absolute', top: '14.5%', left: '24.75%', width: '50.5%', height: '42%', overflow: 'hidden', borderRadius: 2, background: '#f3f4f6' }}>
              <div dir="rtl" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 14, height: 14, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 7, fontWeight: 800 }}>₪</div><span style={{ fontSize: 9, fontWeight: 700, color: '#374151' }}>פיננסים</span></div>
                  <div style={{ display: 'flex', gap: 2 }}>{['הכנסה ➕','הוצאה ➖','דוח 📊'].map(t => (<span key={t} style={{ fontSize: 6, fontWeight: 700, color: '#fff', padding: '1px 4px', borderRadius: 9999, background: t.includes('הכנסה') ? '#22c55e' : t.includes('הוצאה') ? '#ef4444' : '#6366f1' }}>{t}</span>))}</div>
                </div>
                <div dir="rtl" style={{ background: '#fff', padding: '4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}><span style={{ fontWeight: 900, color: '#1f2937', fontSize: 11 }}>פיננסים</span><span style={{ background: '#16a34a', color: '#fff', borderRadius: 6, padding: '2px 6px', fontSize: 7, fontWeight: 700 }}>+ הוסף עסקה</span></div>
              </div>
              <div className="app-scroll" style={{ willChange: 'transform' }}>
                <div dir="rtl" style={{ background: 'linear-gradient(135deg,#15803d,#16a34a)', margin: 6, borderRadius: 8, padding: '8px 10px', color: '#fff' }}>
                  <p style={{ fontSize: 8, fontWeight: 700, marginBottom: 6, opacity: 0.9 }}>📊 לוח תקציב מסכם — 2026</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>{[{ label: 'הכנסות מתוכננות', val: '₪701,006' },{ label: 'הוצאות מתוכננות', val: '₪86,029' },{ label: 'עודף תכנוני', val: '+₪614,977' }].map(k => (<div key={k.label} style={{ textAlign: 'center' }}><p style={{ fontSize: 8, fontWeight: 900 }}>{k.val}</p><p style={{ fontSize: 5.5, opacity: 0.8, marginTop: 1 }}>{k.label}</p></div>))}</div>
                </div>
                <div dir="rtl" style={{ margin: '0 6px 6px', background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                  <div style={{ background: '#f9fafb', padding: '4px 8px', borderBottom: '1px solid #e5e7eb' }}><p style={{ fontSize: 8, fontWeight: 700, color: '#1f2937' }}>לוח תקציב חודשי</p></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '3px 6px', background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>{['סעיף','ינו','מרץ','מאי','יול','ספט','שנתי'].map(h => (<span key={h} style={{ fontSize: 5.5, fontWeight: 700, color: '#6b7280', textAlign: h === 'סעיף' ? 'right' : 'center' }}>{h}</span>))}</div>
                  {[{ name: 'תרומות', color: '#15803d', vals: ['6k','67k','70k','66k','67k','67k','₪701k'] },{ name: 'תשלומי הורים', color: '#15803d', vals: ['80k','78k','56k','66k','67k','67k','₪695k'] },{ name: 'שכר עובדים', color: '#dc2626', vals: ['6k','6k','6k','6k','6k','6k','₪46k'] },{ name: 'שכירות ותחזוקה', color: '#dc2626', vals: ['6k','1k','0k','0k','4k','4k','₪33k'] },{ name: 'אירועים', color: '#f59e0b', vals: ['0k','0k','0k','0k','0k','0k','₪7k'] }].map((row, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '3px 6px', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}><div style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: row.color, flexShrink: 0 }} /><span style={{ fontSize: 6, color: '#1f2937', fontWeight: 600 }}>{row.name}</span></div>{row.vals.map((v, j) => (<span key={j} style={{ fontSize: j === 6 ? 6.5 : 5.5, color: j === 6 ? row.color : '#374151', fontWeight: j === 6 ? 700 : 400, textAlign: 'center' }}>{v}</span>))}</div>
                  ))}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '4px 6px', background: '#1f2937' }}><span style={{ fontSize: 6.5, fontWeight: 900, color: '#fff' }}>מאזן חודשי</span>{['80k+','66k+','64k+','65k+','55k+','56k+','₪615k+'].map((v,i) => (<span key={i} style={{ fontSize: i === 6 ? 6.5 : 5.5, color: '#4ade80', fontWeight: 700, textAlign: 'center' }}>{v}</span>))}</div>
                </div>
                <div dir="rtl" style={{ margin: '0 6px 6px', background: '#fff', borderRadius: 8, padding: '6px 8px', border: '1px solid #e5e7eb' }}>
                  <p style={{ fontSize: 7, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>הוצאות בפועל מול תקציב</p>
                  {[{ label: 'שכר עובדים', actual: 78, val: '₪46k', color: '#6366f1' },{ label: 'שכירות', actual: 55, val: '₪33k', color: '#f59e0b' },{ label: 'אירועים', actual: 30, val: '₪7k', color: '#ec4899' }].map(r => (
                    <div key={r.label} style={{ marginBottom: 4 }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}><span style={{ fontSize: 6, color: '#374151' }}>{r.label}</span><span style={{ fontSize: 6, fontWeight: 700, color: r.color }}>{r.val}</span></div><div style={{ height: 4, background: '#f3f4f6', borderRadius: 9999, overflow: 'hidden' }}><div style={{ height: '100%', width: `${r.actual}%`, background: r.color, borderRadius: 9999 }} /></div></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── iPhone mockup image ── */}
          <div className="relative flex-shrink-0" style={{ width: '60%', marginBottom: 24, marginRight: '-45%', zIndex: 10 }}>
            <div className="text-center mb-4"><p style={{ fontSize: 11, fontWeight: 800, color: '#fff', lineHeight: 1.4 }}>הגדר יעד.<br/>הכנס נתונים.<br/>צא לדרך. 🚀</p><p style={{ fontSize: 9, color: '#bfdbfe', marginTop: 4, lineHeight: 1.5 }}>קמפיין גיוס מוכן<br/>תוך דקות</p></div>
            <div style={{ position: 'relative' }}>
              <img src="/iphone-mockup-new.png" alt="Amutech Mobile" style={{ width: '100%', height: 'auto', filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.4))', position: 'relative', zIndex: 2, pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', top: '10%', left: '38.2%', width: '23.5%', height: '72%', overflow: 'hidden', borderRadius: 8, background: '#f9fafb', zIndex: 1 }}>
                <div dir="rtl" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', padding: '10px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ color: '#fff', fontWeight: 700, fontSize: 9 }}>✨ קמפיין חדש</span><span style={{ color: '#e0e7ff', fontSize: 7 }}>שלב 1 מתוך 4</span></div>
                <div style={{ overflow: 'hidden', background: '#f8fafc', height: 380 }}><div className="phone-scroll" style={{ willChange: 'transform', padding: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ background: '#fff', borderRadius: 8, padding: '6px 8px', border: '1.5px solid #6366f1' }}><p style={{ fontSize: 6, fontWeight: 700, color: '#6366f1', marginBottom: 3 }}>שלב 1 — פרטי הקמפיין</p><div style={{ background: '#f3f4f6', borderRadius: 5, padding: '3px 6px', marginBottom: 3 }}><p style={{ fontSize: 5.5, color: '#9ca3af', marginBottom: 1 }}>שם הקמפיין</p><p style={{ fontSize: 7, color: '#1f2937', fontWeight: 600 }}>מצילים את מאור 💙</p></div><div style={{ background: '#f3f4f6', borderRadius: 5, padding: '3px 6px' }}><p style={{ fontSize: 5.5, color: '#9ca3af', marginBottom: 1 }}>יעד גיוס</p><p style={{ fontSize: 7, color: '#1f2937', fontWeight: 600 }}>₪100,000</p></div></div>
                  <div style={{ background: '#fff', borderRadius: 8, padding: '6px 8px', border: '1px solid #e5e7eb' }}><p style={{ fontSize: 6, fontWeight: 700, color: '#8b5cf6', marginBottom: 3 }}>שלב 2 — תוכן AI 🤖</p><div style={{ background: 'linear-gradient(135deg,#eef2ff,#faf5ff)', borderRadius: 5, padding: '4px 6px', marginBottom: 3 }}><p style={{ fontSize: 6, color: '#4338ca', lineHeight: 1.4 }}>"מאור בן 8, נלחם בגבורה..."</p></div></div>
                  <div style={{ background: '#fff', borderRadius: 8, padding: '6px 8px', border: '1px solid #e5e7eb' }}><p style={{ fontSize: 6, fontWeight: 700, color: '#f59e0b', marginBottom: 3 }}>שלב 3 — שגרירים 🏆</p>{[{ name: 'יוסי כהן', pct: 42 },{ name: 'רחל לוי', pct: 31 },{ name: 'דוד מזרחי', pct: 19 }].map(a => (<div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}><div style={{ width: 14, height: 14, borderRadius: '50%', background: 'linear-gradient(135deg,#f59e0b,#ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 6, fontWeight: 700, flexShrink: 0 }}>{a.name[0]}</div><div style={{ flex: 1 }}><span style={{ fontSize: 6, color: '#1f2937', fontWeight: 600 }}>{a.name}</span><div style={{ height: 3, background: '#f3f4f6', borderRadius: 9999, marginTop: 2 }}><div style={{ height: '100%', width: `${a.pct}%`, background: '#f59e0b', borderRadius: 9999 }} /></div></div></div>))}</div>
                  <div style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: 8, padding: '8px', textAlign: 'center' }}><p style={{ fontSize: 7, fontWeight: 900, color: '#fff', marginBottom: 4 }}>🚀 הקמפיין מוכן!</p><div style={{ background: '#fbbf24', borderRadius: 6, padding: '3px 0' }}><p style={{ fontSize: 7, fontWeight: 700, color: '#1f2937' }}>פרסם עכשיו ✅</p></div></div>
                </div></div>
                <div style={{ background: '#fff', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-around', padding: '4px 0' }}>{['🏠','❤️','📊','👥'].map(ic => <span key={ic} style={{ fontSize: 9 }}>{ic}</span>)}</div>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 1.7 — REAL SCREENSHOTS
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section ref={shotsSection.ref} className="relative py-20 px-6 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-700 ${shotsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
              מבט אמיתי פנימה
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-4">
              מסכים אמיתיים. מספרים אמיתיים.
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              ככה נראית עמותה שמנהלת הכל במקום אחד — בלי פוטושופ.
            </p>
          </div>

          {/* Main shot — income dashboard in a browser frame */}
          <div className={`relative transition-all duration-1000 ${shotsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className="absolute -inset-4 bg-gradient-to-l from-blue-200/40 via-violet-200/40 to-emerald-200/40 blur-2xl rounded-full pointer-events-none" />
            <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-[0_40px_90px_-25px_rgba(30,58,138,0.35)] hover:shadow-[0_50px_110px_-25px_rgba(30,58,138,0.45)] transition-all duration-500 hover:scale-[1.01]">
              <div dir="ltr" className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="w-full max-w-[240px] rounded-md bg-white border border-gray-200 text-gray-400 text-[11px] text-center py-1 font-medium">app.amutech.co.il — הכנסות ותרומות</div>
                </div>
                <div className="w-10" />
              </div>
              <img src="/screenshots/income-hero.png" alt="מסך הכנסות ותרומות — עמוטק" loading="lazy" className="w-full h-auto block" />
            </div>
          </div>

          {/* Two secondary shots */}
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {[
              { src: '/screenshots/budget.png', alt: 'לוח תקציב שנתי — עמוטק', title: '📊 תקציב שנתי חכם', desc: 'תכנון מול ביצוע, סעיף-סעיף, חודש-חודש.', delay: 150 },
              { src: '/screenshots/money-flow.png', alt: 'תנועת כספים — עמוטק', title: '📈 תנועת כספים חיה', desc: 'הכנסות מול הוצאות בזמן אמת — 7/30/90 ימים.', delay: 300 },
            ].map(s => (
              <div
                key={s.src}
                className={`group rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-lg hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-500 ${shotsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${s.delay}ms` }}
              >
                <div className="overflow-hidden">
                  <img src={s.src} alt={s.alt} loading="lazy" className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-700" />
                </div>
                <div className="px-6 py-4 border-t border-gray-100">
                  <p className="font-bold text-gray-900">{s.title}</p>
                  <p className="text-sm text-gray-500">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mini feature shots — telephony, mailing, calendar */}
          <p className={`mt-12 mb-5 text-center text-sm font-bold tracking-[0.2em] text-gray-400 transition-all duration-700 ${shotsSection.isVisible ? 'opacity-100' : 'opacity-0'}`}>
            — ועוד בפנים —
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { src: '/screenshots/mail-builder.png', alt: 'בונה הדיוור — עמוטק', title: '💌 בונה דיוור בגרירה', desc: 'מיילים מעוצבים עם תבניות מוכנות.', delay: 200, pos: 'object-top' },
              { src: '/screenshots/telephony.png', alt: 'מוקד טלפוני — עמוטק', title: '📞 מוקד טלפוני מובנה', desc: 'רשימות חיוג, הקצאות ו-AI Rescore.', delay: 320, pos: 'object-right' },
              { src: '/screenshots/calendar.png', alt: 'לוח שנה ואירועים — עמוטק', title: '🗓️ לוח שנה עברי-לועזי', desc: 'אירועים, חגים וסנכרון Google Calendar.', delay: 440, pos: 'object-top' },
            ].map(s => (
              <div
                key={s.src}
                className={`group rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-md hover:shadow-xl hover:-translate-y-1.5 hover:rotate-[0.4deg] transition-all duration-500 ${shotsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${s.delay}ms` }}
              >
                <div className="overflow-hidden h-40 bg-gray-50">
                  <img src={s.src} alt={s.alt} loading="lazy" className={`w-full h-full object-cover ${s.pos} group-hover:scale-[1.05] transition-transform duration-700`} />
                </div>
                <div className="px-5 py-3.5 border-t border-gray-100">
                  <p className="font-bold text-gray-900 text-sm">{s.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 2.5 — PAIN POINTS
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-red-100 text-red-600 text-sm font-semibold rounded-full mb-4">
              מה מכאיב לעמותות?
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
              אנחנו מכירים את הכאבים שלכם
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              כל עמותה מתמודדת עם אותן בעיות — אנחנו פתרנו אותן.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                pain: '😤 ניהול בגיליונות אקסל',
                desc: 'עשרות קבצים, נתונים מפוזרים, טעויות ידניות — כל דוח לוקח שעות.',
                fix: 'כל הנתונים במקום אחד, דוחות בלחיצה.',
                color: 'red',
              },
              {
                pain: '📧 לא יודעים כמה גייסנו',
                desc: 'קמפיין רץ אבל אין מעקב בזמן אמת — מי תרם? כמה נשאר ליעד?',
                fix: 'דשבורד חי עם כל המספרים, תמיד עדכני.',
                color: 'orange',
              },
              {
                pain: '🤯 גיוס כסף מתיש',
                desc: 'לבנות דף תרומה, לשלוח מיילים, לנהל שגרירים — הכל ידנית.',
                fix: 'קמפיין מוכן עם AI תוך 5 דקות, שגרירים ניהול עצמי.',
                color: 'purple',
              },
            ].map((p) => {
              const border = p.color === 'red' ? 'border-red-200' : p.color === 'orange' ? 'border-orange-200' : 'border-purple-200';
              const bg     = p.color === 'red' ? 'bg-red-50'     : p.color === 'orange' ? 'bg-orange-50'     : 'bg-purple-50';
              const fix    = p.color === 'red' ? 'text-red-600'  : p.color === 'orange' ? 'text-orange-600'  : 'text-purple-600';
              const fixbg  = p.color === 'red' ? 'bg-red-100'    : p.color === 'orange' ? 'bg-orange-100'    : 'bg-purple-100';
              return (
                <div key={p.pain} className={`rounded-2xl border-2 ${border} ${bg} p-6`}>
                  <p className="text-xl font-black text-gray-900 mb-3">{p.pain}</p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{p.desc}</p>
                  <div className={`${fixbg} rounded-xl px-4 py-2.5 flex items-start gap-2`}>
                    <span className="text-green-500 font-bold text-sm mt-0.5">✓</span>
                    <p className={`text-sm font-semibold ${fix}`}>{p.fix}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Transition arrow */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-full font-bold text-sm shadow-lg">
              <span>הפתרון לכל זה?</span>
              <span className="text-lg">👇</span>
              <span>Amutech</span>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 3 — FEATURES GRID
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div
            ref={featuresHeader.ref}
            className={`text-center mb-16 transition-all duration-700 ${
              featuresHeader.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
              הפיצ&apos;רים שלנו
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
              כל מה שעמותה צריכה
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              כלים מקצועיים לניהול תרומות, קמפיינים, אנשי קשר ועוד — הכל בממשק אחד פשוט ונוח.
            </p>
          </div>

          {/* Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {FEATURES.map((feature, i) => {
              const colors = FEATURE_COLORS[feature.color];
              const Icon = feature.icon;
              const { ref, isVisible } = featureRefs[i];
              return (
                <div
                  key={i}
                  ref={ref}
                  className={`group p-7 rounded-3xl bg-white border border-gray-200 ${colors.border} hover:shadow-xl hover:-translate-y-1 transition-all duration-500 transform ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div
                    className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon size={28} className={colors.icon} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-[15px]">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 4 — HOW IT WORKS
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div
            ref={stepsSection.ref}
            className={`text-center mb-16 transition-all duration-700 ${
              stepsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-4">
              איך מתחילים?
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
              3 צעדים פשוטים להצלחה
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              מהרשמה ועד הקמפיין הראשון — תוך פחות מ-10 דקות. בלי כאבי ראש, בלי ייעוץ יקר.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-24 right-[16.5%] left-[16.5%] h-0.5 bg-gradient-to-l from-blue-300 via-purple-300 to-blue-300" />

            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={i}
                  className={`relative text-center transition-all duration-700 ${
                    stepsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${i * 200}ms` }}
                >
                  <div className="relative z-10 w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg">
                    <Icon size={32} className="text-white" />
                  </div>
                  <span className="inline-block text-sm font-bold text-purple-600 mb-2">
                    שלב {step.num}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  {step.badge && (
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full mb-2">
                      ✓ {step.badge}
                    </span>
                  )}
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 5 — PRICING
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="pricing" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            ref={pricingSection.ref}
            className={`text-center mb-12 transition-all duration-700 ${
              pricingSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
              תמחור
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
              תוכנית לכל עמותה
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto mb-8">
              בחרו את התוכנית שמתאימה לכם. כל התוכניות כוללות 7 ימי ניסיון חינם.
            </p>

            {/* Monthly/Yearly Toggle */}
            <div className="inline-flex items-center gap-3 bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  !isYearly
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                חודשי
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  isYearly
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                שנתי
                <span className="mr-1 text-xs text-emerald-600 font-bold">חסכו 15%</span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {PRICING.map((tier, i) => (
              <div
                key={i}
                className={`relative flex flex-col rounded-3xl border-2 p-8 transition-all duration-500 ${
                  tier.popular
                    ? 'border-blue-600 bg-gradient-to-b from-blue-50/60 to-white shadow-2xl shadow-blue-600/10 ring-4 ring-blue-100 scale-[1.02]'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg'
                } ${
                  pricingSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {tier.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-l from-blue-600 to-indigo-600 text-white text-xs font-bold rounded-full shadow-md whitespace-nowrap">
                    ⭐ הכי פופולרי
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{tier.name}</h3>
                  <p className="text-sm text-gray-500">{tier.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black text-gray-900">
                      &#8362;{isYearly ? tier.yearlyPrice : tier.price}
                    </span>
                    <span className="text-gray-500 font-medium">/ חודש</span>
                  </div>
                  {isYearly && (
                    <p className="text-sm text-emerald-600 font-medium mt-1">
                      חיסכון של &#8362;{(tier.price - tier.yearlyPrice) * 12} בשנה
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((feat, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <CheckCircle
                        size={18}
                        className={`flex-shrink-0 mt-0.5 ${
                          tier.popular ? 'text-blue-600' : 'text-emerald-500'
                        }`}
                      />
                      <span className="text-gray-700 text-[15px]">{feat}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/register"
                  className={`block text-center py-3.5 rounded-2xl font-bold transition-all duration-300 hover:scale-[1.02] ${
                    tier.popular
                      ? 'bg-gradient-to-l from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-600/25'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  פתח חשבון בחינם
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 7 — FAQ
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="faq" className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div
            ref={faqSection.ref}
            className={`text-center mb-12 transition-all duration-700 ${
              faqSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
              שאלות נפוצות
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
              יש שאלות? יש תשובות.
            </h2>
          </div>

          <div
            className={`bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm transition-all duration-700 ${
              faqSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {FAQ_ITEMS.map((item, i) => (
              <FaqAccordionItem
                key={i}
                item={item}
                isOpen={openFaq === i}
                onToggle={() => toggleFaq(i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 7.5 — אודות / About (merchant identity for PCI compliance)
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section id="about" className="py-24 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto" dir="rtl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
              אודות
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
              מי אנחנו
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              עמוטק היא פלטפורמת ניהול ייעודית לעמותות ולארגוני המגזר השלישי בישראל —
              ניהול תרומות, סליקת אשראי, קמפיינים, CRM, מתנדבים ודיוור, הכל במערכת אחת.
              אנחנו דואגים לעמוד התשתית הטכנולוגי כדי שתוכלו להתמקד בלגייס תרומות ולהוביל שינוי.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company info card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield size={20} className="text-blue-600" />
                פרטי החברה
              </h3>
              <dl className="space-y-3 text-sm text-gray-700">
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">שם רשום</dt>
                  <dd className="font-semibold text-gray-900">
                    Y.Sh כלכלה וצריכה חברתית בע&quot;מ
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">מספר ח.פ.</dt>
                  <dd className="font-semibold text-gray-900">516866514</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">כתובת רשומה</dt>
                  <dd className="font-semibold text-gray-900">עציון 27, פרדס חנה</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">מותג מסחרי</dt>
                  <dd className="font-semibold text-gray-900">עמוטק / Amutech</dd>
                </div>
              </dl>
            </div>

            {/* Contact info card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Phone size={20} className="text-blue-600" />
                יצירת קשר
              </h3>
              <dl className="space-y-3 text-sm text-gray-700">
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">טלפון / WhatsApp</dt>
                  <dd>
                    <a
                      href="tel:0538286227"
                      className="font-semibold text-blue-700 hover:text-blue-800 transition-colors"
                      dir="ltr"
                    >
                      053-828-6227
                    </a>
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">דוא&quot;ל תמיכה</dt>
                  <dd>
                    <a
                      href="mailto:support@amutech.co.il"
                      className="font-semibold text-blue-700 hover:text-blue-800 transition-colors"
                      dir="ltr"
                    >
                      support@amutech.co.il
                    </a>
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">מסמכים משפטיים</dt>
                  <dd className="flex gap-3">
                    <Link to="/terms" className="font-semibold text-blue-700 hover:text-blue-800 transition-colors">
                      תקנון
                    </Link>
                    <span className="text-gray-300">|</span>
                    <Link to="/privacy" className="font-semibold text-blue-700 hover:text-blue-800 transition-colors">
                      מדיניות פרטיות
                    </Link>
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500 mb-0.5">סליקה מאובטחת</dt>
                  <dd className="font-semibold text-gray-900">PCI-DSS · טרנזילה</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 8 — FINAL CTA
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-24 px-6 bg-[#0A1130] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-600/30 rounded-full blur-[130px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[450px] h-[450px] bg-violet-600/25 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3" />

        <div
          ref={ctaSection.ref}
          className={`relative max-w-3xl mx-auto text-center transition-all duration-700 ${
            ctaSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-white/10 border border-white/20 rounded-full">
            <span className="text-amber-400 text-sm">⚡</span>
            <span className="text-white/90 text-sm font-semibold">7 ימים לנסות — אפס סיכון, אפס כרטיס אשראי</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-5">
            מוכנים להפסיק לבזבז זמן<br/>
            <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
              ולהתחיל לגייס יותר?
            </span>
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed max-w-xl mx-auto mb-10">
            נהלו תרומות, קמפיינים ותקציב — הכל במקום אחד, בפשטות ובדיוק.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-l from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-gray-900 text-lg font-bold rounded-2xl shadow-[0_8px_30px_rgba(245,158,11,0.35)] hover:shadow-[0_12px_40px_rgba(245,158,11,0.45)] transition-all duration-300 hover:scale-[1.03]"
            >
              פתח חשבון בחינם עכשיו
              <ArrowLeft size={20} />
            </Link>
            <a
              href="#features"
              className="inline-flex items-center justify-center gap-2 px-8 py-5 bg-white/[0.06] border border-white/20 text-white text-base font-semibold rounded-2xl hover:bg-white/[0.12] hover:border-white/30 transition-all duration-300"
            >
              ראה את הפיצ׳רים
              <ChevronDown size={18} />
            </a>
          </div>

          {/* Mini trust row */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-blue-200 text-sm mb-6">
            {['✅ ביטול בכל עת', '🇮🇱 שרתים בישראל', '🔒 מאובטח ומוצפן', '📞 תמיכה בעברית'].map(t => (
              <span key={t} className="flex items-center gap-1">{t}</span>
            ))}
          </div>

          <p className="text-blue-300 text-sm">
            שאלות? <a href="mailto:support@amutech.co.il" className="text-white underline hover:text-amber-300 transition-colors">support@amutech.co.il</a>
          </p>
        </div>
      </section>

      {/* ── Floating WhatsApp button ── */}
      <a
        href="https://wa.me/972538286227?text=שלום%2C%20אני%20מעוניין%20לשמוע%20עוד%20על%20Amutech"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-4 py-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
        style={{ direction: 'rtl' }}
      >
        {/* WhatsApp SVG icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="22" height="22" fill="white">
          <path d="M16 2C8.28 2 2 8.28 2 16c0 2.46.67 4.86 1.94 6.97L2 30l7.26-1.9A13.93 13.93 0 0 0 16 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.5a11.45 11.45 0 0 1-5.84-1.6l-.42-.25-4.31 1.13 1.15-4.2-.27-.44A11.47 11.47 0 0 1 4.5 16C4.5 9.6 9.6 4.5 16 4.5S27.5 9.6 27.5 16 22.4 27.5 16 27.5zm6.29-8.56c-.34-.17-2.02-1-2.34-1.11-.32-.11-.55-.17-.78.17-.23.34-.89 1.11-1.09 1.34-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.74-1.69-1.01-.9-1.7-2.01-1.9-2.35-.2-.34-.02-.52.15-.69.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6-.09-.17-.78-1.88-1.07-2.57-.28-.67-.57-.58-.78-.59l-.67-.01c-.23 0-.6.09-.91.43-.31.34-1.19 1.16-1.19 2.84 0 1.68 1.22 3.3 1.39 3.53.17.23 2.4 3.67 5.82 5.14.81.35 1.45.56 1.94.72.82.26 1.56.22 2.15.13.66-.1 2.02-.82 2.31-1.62.28-.8.28-1.48.2-1.62-.09-.14-.32-.23-.66-.4z"/>
        </svg>
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-500 whitespace-nowrap">
          פנו אלינו
        </span>
      </a>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          SECTION 9 — FOOTER
         ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <footer className="py-16 px-6 bg-[#070C22] text-gray-400 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          {/* Top row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-14">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-xl font-black text-white mb-3">עמוטק</h3>
              <p className="text-sm leading-relaxed mb-4">
                פלטפורמת ניהול העמותות המתקדמת בישראל. תרומות, קמפיינים, CRM ועוד.
              </p>

              {/* Merchant of record / company details — required for credit-card clearing compliance */}
              <div className="text-xs leading-relaxed text-gray-500 mb-4 space-y-0.5">
                <div className="text-gray-300 font-semibold">Y.Sh כלכלה וצריכה חברתית בע&quot;מ</div>
                <div>ח.פ. 516866514</div>
                <div>עציון 27, פרדס חנה</div>
                <div>
                  טלפון:{' '}
                  <a href="tel:0538286227" className="hover:text-white transition-colors" dir="ltr">
                    053-828-6227
                  </a>
                </div>
                <div>
                  דוא&quot;ל:{' '}
                  <a
                    href="mailto:support@amutech.co.il"
                    className="hover:text-white transition-colors"
                    dir="ltr"
                  >
                    support@amutech.co.il
                  </a>
                </div>
              </div>

              {/* Social Icons (placeholders) */}
              <div className="flex gap-3">
                {[Globe, MessageCircle, Mail].map((Icon, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-blue-600 flex items-center justify-center transition-colors duration-300"
                    aria-label="social link"
                  >
                    <Icon size={16} className="text-gray-400 hover:text-white" />
                  </a>
                ))}
              </div>
            </div>

            {/* Link columns */}
            {Object.values(FOOTER_LINKS).map((col, i) => (
              <div key={i}>
                <h4 className="text-white font-bold text-sm mb-4">{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.links.map((link, j) => (
                    <li key={j}>
                      {link.href.startsWith('/') ? (
                        <Link
                          to={link.href}
                          className="text-sm hover:text-white transition-colors duration-200"
                        >
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-sm hover:text-white transition-colors duration-200"
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm">&copy; 2026 עמוטק. כל הזכויות שמורות.</p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors duration-200">
                מדיניות פרטיות
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors duration-200">
                תנאי שימוש
              </Link>
              <div className="flex items-center gap-2">
                <Shield size={14} />
                <span>מאובטח ומוגן SSL</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Global animation styles ── */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 200ms;
          opacity: 0;
        }
        .animation-delay-400 {
          animation-delay: 400ms;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

/* ──────────────────────────────────────────────
   DocumentHead — set page title and meta tags
   via useEffect (no react-helmet dependency)
   ────────────────────────────────────────────── */
const DocumentHead: React.FC = () => {
  useEffect(() => {
    document.title = 'עמוטק — ניהול עמותות חכם | תרומות, קמפיינים, CRM ו-AI';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta(
      'description',
      'עמוטק — פלטפורמת ניהול עמותות מתקדמת. ניהול תרומות, קמפיינים, CRM, דיוור ובינה מלאכותית במערכת אחת.'
    );
    setOg('og:title', 'עמוטק — ניהול עמותות חכם');
    setOg(
      'og:description',
      'תרומות, קמפיינים, CRM, דיוור ו-AI — הכל במערכת אחת לעמותות ישראליות.'
    );
    setOg('og:image', '/og-image.png');
    setOg('og:type', 'website');
  }, []);

  return null;
};

export default LandingPageV2;
